import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Clock, Users, MapPin, ArrowRight, Filter, Trophy, GraduationCap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'all', label: 'All Courses' },
  { value: 'JEE Mains', label: 'JEE Mains' },
  { value: 'JEE Advanced', label: 'JEE Advanced' },
  { value: 'NEET UG', label: 'NEET UG' },
  { value: 'NEET PG', label: 'NEET PG' },
  { value: 'Foundation', label: 'Foundation' },
];

const courseTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'Online', label: 'Online' },
  { value: 'Offline', label: 'Offline' },
];

const CoursesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/courses`;
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') params.append('category', selectedCategory);
        if (selectedType !== 'all') params.append('course_type', selectedType);

        if (params.toString()) url += `?${params.toString()}`;

        const response = await axios.get(url);
        setCourses(response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [selectedCategory, selectedType]);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    const params = new URLSearchParams(searchParams);
    if (value === 'all') {
      params.delete('category');
    } else {
      params.set('category', value);
    }
    setSearchParams(params);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    const params = new URLSearchParams(searchParams);
    if (value === 'all') {
      params.delete('type');
    } else {
      params.set('type', value);
    }
    setSearchParams(params);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4">Our Curriculum</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-slate-900 leading-tight">
              Explore Our Courses: <span className="text-primary">Unlock Your Potential</span> with the Right Learning Path
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Programs Designed for Academic Excellence and Career Success
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                <Link to="/contact">Get Free Consultation</Link>
              </Button>
              <Button size="lg" variant="outline" onClick={() => toast.success('Brochure download starting shortly! Please contact us at +91 9595253778 if you have any questions.')}>
                Get Brochure
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Programs Overview Section */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Programs Designed for Academic Excellence and Career Success
            </h2>
            <p className="text-muted-foreground">
              At Caliber Nova, we offer comprehensive coaching programs that help students build strong academic foundations, excel in board examinations, and prepare for competitive and professional career paths. Our structured learning approach, experienced faculty, and personalized guidance ensure that every student receives the support needed to achieve their goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Foundation Coaching */}
            <Card className="border shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
              <CardContent className="p-6 md:p-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <GraduationCap className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900">Foundation Coaching (Classes 7th to 10th)</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Our Foundation Program is designed to strengthen conceptual understanding, analytical thinking, and problem-solving abilities from an early stage. By focusing on academic fundamentals, we help students perform confidently in school examinations while preparing them for future competitive challenges.
                </p>
                <div className="space-y-2 pt-2">
                  <p className="font-semibold text-slate-800 text-sm">Key Benefits:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-600 text-xs">
                    <li className="flex items-center gap-1.5">✓ Concept clarity & fundamentals</li>
                    <li className="flex items-center gap-1.5">✓ Personalized tracking</li>
                    <li className="flex items-center gap-1.5">✓ Interactive practice-based teaching</li>
                    <li className="flex items-center gap-1.5">✓ Regular tests & analysis</li>
                    <li className="flex items-center gap-1.5">✓ Doubt-solving & mentoring</li>
                    <li className="flex items-center gap-1.5">✓ Preparation for competitive exams</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* 11th & 12th Science and Commerce */}
            <Card className="border shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
              <CardContent className="p-6 md:p-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900">11th & 12th Science and Commerce</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We provide comprehensive coaching for Science and Commerce students, helping them excel in board examinations and build a solid foundation for higher education and career opportunities.
                </p>
                <div className="space-y-2 pt-2">
                  <p className="font-semibold text-slate-800 text-sm">Key Benefits:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-600 text-xs">
                    <li className="flex items-center gap-1.5">✓ Experienced faculty for both streams</li>
                    <li className="flex items-center gap-1.5">✓ Board-focused preparation</li>
                    <li className="flex items-center gap-1.5">✓ Regular tests & performance evaluation</li>
                    <li className="flex items-center gap-1.5">✓ Subject-wise doubt-solving</li>
                    <li className="flex items-center gap-1.5">✓ Comprehensive study material</li>
                    <li className="flex items-center gap-1.5">✓ Academic & career planning</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Competitive Exam Preparation */}
            <Card className="border shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
              <CardContent className="p-6 md:p-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900">Competitive Exam Preparation</h3>
                    <p className="text-xs text-primary font-semibold mt-0.5">JEE, NEET, MHT-CET, CLAT, IPMAT</p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Our competitive exam programs are designed to help students develop the knowledge, strategy, and confidence required to succeed in highly competitive entrance examinations.
                </p>
                <div className="space-y-2 pt-2">
                  <p className="font-semibold text-slate-800 text-sm">Key Benefits:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-600 text-xs">
                    <li className="flex items-center gap-1.5">✓ Concept-based structured preparation</li>
                    <li className="flex items-center gap-1.5">✓ Mock tests & performance analysis</li>
                    <li className="flex items-center gap-1.5">✓ Dedicated doubt-solving sessions</li>
                    <li className="flex items-center gap-1.5">✓ Exam-oriented study resources</li>
                    <li className="flex items-center gap-1.5">✓ Accuracy & problem-solving focus</li>
                    <li className="flex items-center gap-1.5">✓ Personalized mentoring</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Professional Courses */}
            <Card className="border shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
              <CardContent className="p-6 md:p-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900">Professional Courses</h3>
                    <p className="text-xs text-primary font-semibold mt-0.5">CA, CS, CMA, ACCA, CFA, MBA-CET, MBA, BBA, B.Com, BCA, BCCA</p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Caliber Nova also offers coaching and guidance for professional and career-oriented programs that help students advance toward successful careers in commerce, management, finance, and related fields.
                </p>
                <div className="space-y-2 pt-2">
                  <p className="font-semibold text-slate-800 text-sm">Key Benefits:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-600 text-xs">
                    <li className="flex items-center gap-1.5">✓ Career-focused learning approach</li>
                    <li className="flex items-center gap-1.5">✓ Emphasis on practical application</li>
                    <li className="flex items-center gap-1.5">✓ Regular assessments & tracking</li>
                    <li className="flex items-center gap-1.5">✓ Personalized academic guidance</li>
                    <li className="flex items-center gap-1.5">✓ Experienced faculty mentors</li>
                    <li className="flex items-center gap-1.5">✓ Long-term career success prep</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b sticky top-16 bg-white z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Category Tabs */}
            <Tabs value={selectedCategory} onValueChange={handleCategoryChange} className="w-full md:w-auto">
              <TabsList className="h-auto flex-wrap justify-start bg-transparent p-0 gap-2">
                {categories.map((cat) => (
                  <TabsTrigger
                    key={cat.value}
                    value={cat.value}
                    data-testid={`filter-${cat.value}`}
                    className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-full px-4 py-2 text-sm"
                  >
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedType} onValueChange={handleTypeChange}>
                <SelectTrigger className="w-[140px]" data-testid="type-filter">
                  <SelectValue placeholder="Course Type" />
                </SelectTrigger>
                <SelectContent>
                  {courseTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Course Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-slate-200"></div>
                  <CardContent className="p-6">
                    <div className="h-4 bg-slate-200 rounded mb-2 w-20"></div>
                    <div className="h-6 bg-slate-200 rounded mb-4"></div>
                    <div className="h-4 bg-slate-200 rounded mb-2"></div>
                    <div className="h-10 bg-slate-200 rounded mt-4"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : courses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card className="h-full card-hover overflow-hidden group" data-testid={`course-${course.id}`}>
                    <div className="h-48 bg-gradient-to-br from-primary via-primary to-accent relative overflow-hidden">
                      {course.image_url ? (
                        <img src={course.image_url.startsWith('http') ? course.image_url : `${API_URL}${course.image_url.startsWith('/') ? '' : '/'}${course.image_url}`} alt={course.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-white/30" />
                        </div>
                      )}
                      <div className="absolute top-4 left-4 flex gap-2">
                        <Badge className="bg-white/90 text-primary">{course.category}</Badge>
                        {course.scholarship_available && (
                          <Badge className="bg-secondary text-secondary-foreground">Scholarship</Badge>
                        )}
                      </div>
                      {course.is_featured && (
                        <Badge className="absolute top-4 right-4 bg-secondary text-secondary-foreground">Featured</Badge>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-heading font-semibold text-lg mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                        {course.name}
                      </h3>

                      <div className="grid grid-cols-2 gap-3 mb-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{course.duration}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{course.course_type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{course.seats_available} seats</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          <span>{course.batch_timing || 'Flexible'}</span>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {course.description}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div>
                          {course.discounted_fee && course.discounted_fee < course.fee ? (
                            <div className="flex items-center gap-2">
                              <span className="font-heading font-bold text-xl text-primary">
                                ₹{course.discounted_fee.toLocaleString()}
                              </span>
                              <span className="text-sm text-muted-foreground line-through">
                                ₹{course.fee.toLocaleString()}
                              </span>
                            </div>
                          ) : (
                            <span className="font-heading font-bold text-xl text-primary">
                              ₹{course.fee?.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <Button asChild data-testid={`view-course-${course.id}`}>
                          <Link to={`/courses/${course.id}`}>
                            View Details
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Courses Found</h3>
              <p className="text-muted-foreground mb-6">
                No courses match your selected filters. Try adjusting your selection.
              </p>
              <Button onClick={() => { setSelectedCategory('all'); setSelectedType('all'); }}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Have Questions About Admissions or Courses?
          </h2>
          <p className="text-primary-foreground/80 mb-8 text-lg">
            Our academic counselors are here to help you explore programs, understand career pathways, and choose the best learning plan for your goals.
          </p>
          <Button size="lg" variant="secondary" asChild data-testid="cta-contact">
            <Link to="/contact">Book a Free Counseling Session</Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default CoursesPage;
